// ProtectedRoute.js - Route protection components for navigation rules
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasInProgressMealPlan } from '../../utils/inProgressMealPlan';

/**
 * ReviewMealsGuard - Protects /reviewmeals route
 * - Only accessible after completing wizard (wizardCompleted = true OR navigation state has fromWizard)
 * - Also accessible if there's an in-progress meal plan in localStorage (page reload case)
 * - If user navigates directly:
 *   - If they have saved meal plan → redirect to /mymeals
 *   - If no saved meal plan → redirect to /wizard (will consume 1 try)
 */
export const ReviewMealsGuard = ({ children, hasSavedMealPlan, wizardCompleted }) => {
  const location = useLocation();
  
  // Store fromWizard state in a ref to persist across re-renders
  // This handles cases where React Router might clear location.state
  const fromWizardRef = React.useRef(location.state?.fromWizard);
  
  // Update ref if fromWizard becomes true (but never reset it to false during this session)
  if (location.state?.fromWizard) {
    fromWizardRef.current = true;
  }
  
  // If wizard was just completed (either via state or navigation state), allow access
  if (wizardCompleted || fromWizardRef.current) {
    return children;
  }
  
  // If there's an in-progress meal plan in localStorage (page reload case), allow access
  if (hasInProgressMealPlan()) {
    return children;
  }
  
  // If navigating directly without completing wizard and no in-progress data
  if (hasSavedMealPlan) {
    return <Navigate to="/mymeals" replace state={{ from: location }} />;
  }
  
  // No saved meal plan and no in-progress data, redirect to wizard
  // Note: This will consume 1 try when they generate a new meal plan
  return <Navigate to="/wizard" replace state={{ from: location }} />;
};

/**
 * MyMealsGuard - Protects /mymeals route
 * - Only accessible if user has a saved meal plan
 * - If no saved meal plan → redirect to /wizard
 */
export const MyMealsGuard = ({ children, hasSavedMealPlan }) => {
  const location = useLocation();
  
  if (!hasSavedMealPlan) {
    return <Navigate to="/wizard" replace state={{ from: location }} />;
  }
  
  return children;
};

/**
 * WizardGuard - Guards /wizard route
 * - Accessible anytime, but if user has saved meal plan on app load, redirect to /mymeals
 * - This is only for initial load, not when user explicitly navigates to wizard
 */
export const WizardGuard = ({ children, hasSavedMealPlan, isInitialLoad }) => {
  const location = useLocation();
  
  // Only redirect on initial app load, not when user explicitly navigates
  if (isInitialLoad && hasSavedMealPlan && !location.state?.fromStartOver) {
    return <Navigate to="/mymeals" replace state={{ from: location }} />;
  }
  
  return children;
};

/**
 * LandingGuard - Guards / (landing) route
 * - If user has saved meal plan on app load, redirect to /mymeals
 */
export const LandingGuard = ({ children, hasSavedMealPlan, isInitialLoad, isPremium }) => {
  const location = useLocation();
  
  // Premium users should never see the landing page — send them to wizard
  if (isPremium) {
    return <Navigate to="/wizard" replace state={{ from: location }} />;
  }
  
  // Only redirect on initial app load
  if (isInitialLoad && hasSavedMealPlan) {
    return <Navigate to="/mymeals" replace state={{ from: location }} />;
  }
  
  return children;
};
